import type { FlightsAutosuggestResponse } from '../../apis/flights-autosuggest/flightsAutosuggest';
import type { RadarExploreAIResponse } from '../banana/endpoints/radarExploreAI';
import type {
  AdsRetrievalServiceNavCardBodyPayload,
  AdsRetrievalServiceBookingPanelBodyPayload,
} from '../endpoints/adsRetrievalService';
import type { PostcardBodyPayload } from '../endpoints/postcard';
import type { RadarExploreResponse } from '../endpoints/radar/radarExplore';
import type { Slot } from '../endpoints/strevda';
import type { RequestInit, RequestInitHeaders as Headers } from '../internals/types';
import type {
  SkyscannerAdvertsAdsretrievalserviceV1alpha1Request,
  SkyscannerAdvertsAdsretrievalserviceV1alpha1Response,
} from '@skyscanner-internal/ads-retrieval-service-web-http-client';
import type { SupplyAgoraV1DiscountedOffer } from '@skyscanner-internal/agora-web-http-client';
import type { SkyscannerAskskyscannerV1Nudge } from '@skyscanner-internal/ask-skyscanner-web-http-client';
import type { SkyscannerContentdistributionV1MediaDistributionService } from '@skyscanner-internal/cp-distribution-api-web-http-client';
import type { SkyscannerDropsapiV5alphaPricePulse } from '@skyscanner-internal/drops-api-web-http-client';
import type { SkyscannerExploreexperiencessvcV1Experiences } from '@skyscanner-internal/explore-experiences-svc-web-http-client';
import type { SkyscannerExplorepricingserviceV1ExplorePricing } from '@skyscanner-internal/explore-pricing-service-web-http-client';
import type {
  SkyscannerFenryrModelV1PriceCalendar,
  SkyscannerFenryrModelV1PriceCalendarExplore,
  SkyscannerFenryrModelV1PriceCalendarMonth,
} from '@skyscanner-internal/fenryr-web-http-client';
import type {
  SkyscannerLocationtagModelV1,
  SkyscannerLocationtagsserviceApiV1Lookup,
} from '@skyscanner-internal/location-tags-service-web-http-client';
import type { CarhirewebMangalicaV1MangalicaService } from '@skyscanner-internal/mangalica-web-http-client';
import type {
  SkyscannerMichelinRealtimesavingsV1RealTimeSavings,
  SkyscannerMichelinSearchV1Search,
  SkyscannerMichelinV1Packages,
} from '@skyscanner-internal/michelin-web-http-client';
import type { SkyscannerPartnermetadatasvcV1PartnerMetadata } from '@skyscanner-internal/partner-metadata-svc-web-http-client';
import type { SkyscannerPlaceholderserviceV1PlaceholderService } from '@skyscanner-internal/placeholder-service-web-http-client';
import type { SkyscannerPmntdiscountpocsvcV1PaymentDiscount } from '@skyscanner-internal/pmnt-discount-poc-svc-web-http-client';
import type { SkyscannerPriceinsightsV2PriceInsights } from '@skyscanner-internal/price-insights-web-http-client';
import type { SkyscannerRadarAlternativedatesV1AlternativeDates } from '@skyscanner-internal/radar-web-http-client';
import type { SkyscannerRankexplainabilityV1 } from '@skyscanner-internal/rank-explainability-web-http-client';
import type { SkyscannerSonarV3Response } from '@skyscanner-internal/sonar-web-http-client';
import type {
  SkyscannerTerraserviceApiV2Lookup,
  SkyscannerTerraserviceDomainV2Location,
} from '@skyscanner-internal/terra-service-web-http-client';
import type { GatewayServedBy } from '@web-platform/shared-universal-constants/src/conductorHeaders';

export type LocationTag = SkyscannerLocationtagModelV1.Tag;
export type LocationTagResult = SkyscannerLocationtagModelV1.LocationTags;
export type LocationTagsGetRequest =
  SkyscannerLocationtagsserviceApiV1Lookup.GetLocationTagsRequest;
export type LocationTagsGetResponse =
  SkyscannerLocationtagsserviceApiV1Lookup.GetLocationTagsResponse;

// only for Banana
export type ContentPlatformMediaDistributionPayloadResponseError = {
  headers: {};
  body: SkyscannerContentdistributionV1MediaDistributionService.SearchResponse;
  status: number;
  error: true;
};

// only for Banana
export type ContentPlatformMediaDistributionPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerContentdistributionV1MediaDistributionService.SearchResponse;
  status: number;
  error: false;
};

// only for Banana
export type ContentPlatformMediaDistributionPayloadResponse =
  | ContentPlatformMediaDistributionPayloadResponseError
  | ContentPlatformMediaDistributionPayloadResponseSuccess;

export type LoyaltyDiscountsPayloadResponseSuccess = {
  headers: {};
  body: SupplyAgoraV1DiscountedOffer.FetchResponse;
  status: number;
  error: false;
};

export type LoyaltyDiscountsPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type LoyaltyDiscountsPayloadResponse =
  LoyaltyDiscountsPayloadResponseSuccess | LoyaltyDiscountsPayloadResponseError;

export type MangalicaGetPricesForSpanRequestInit = RequestInit<
  CarhirewebMangalicaV1MangalicaService.GetPricesForSpanRequest,
  string
>;

export type MangalicaGetPricesForSpanPayloadResponse =
  MangalicaGetPricesForSpanPayloadResponseSuccess | MangalicaGetPricesForSpanPayloadResponseError;

export type MangalicaGetPricesForSpanPayloadResponseSuccess = {
  headers: Headers;
  body: CarhirewebMangalicaV1MangalicaService.GetPricesForSpanResponse;
  status: number;
  error: false;
};

export type MangalicaGetPricesForSpanPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type SonarV3CreateSessionPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerSonarV3Response.CreateSessionResponse;
  status: number;
  error: false;
};

export type MichelinGetHotelCarHireRequestInit = RequestInit<
  SkyscannerMichelinSearchV1Search.SearchRequest,
  string
>;

export type MichelinGetHotelCarHirePayloadResponse =
  MichelinGetHotelCarHirePayloadResponseSuccess | MichelinGetHotelCarHirePayloadResponseError;

export type MichelinGetHotelCarHirePayloadResponseSuccess = {
  headers: Headers;
  body: SkyscannerMichelinSearchV1Search.SearchResponse;
  status: number;
  error: false;
};

export type MichelinGetHotelCarHirePayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type MichelinGetPackagesRequestInit = RequestInit<
  SkyscannerMichelinV1Packages.PackagesRequest,
  string
>;

export type MichelinGetPackagesPayloadResponse =
  MichelinGetPackagesPayloadResponseSuccess | MichelinGetPackagesPayloadResponseError;

export type MichelinGetPackagesPayloadResponseSuccess = {
  headers: Headers;
  body: SkyscannerMichelinV1Packages.PackagesResponse;
  status: number;
  error: false;
};

export type MichelinGetPackagesPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type MichelinGetPackagesRealTimeSavingsRequestInit = RequestInit<
  SkyscannerMichelinRealtimesavingsV1RealTimeSavings.RealTimeSavingsRequest,
  string
>;

export type MichelinGetPackagesRealTimeSavingsPayloadResponse =
  | MichelinGetPackagesRealTimeSavingsPayloadResponseSuccess
  | MichelinGetPackagesRealTimeSavingsPayloadResponseError;

export type MichelinGetPackagesRealTimeSavingsPayloadResponseSuccess = {
  headers: Headers;
  body: SkyscannerMichelinRealtimesavingsV1RealTimeSavings.RealTimeSavingsResponse;
  status: number;
  error: false;
};

export type MichelinGetPackagesRealTimeSavingsPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type SonarV3PollPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerSonarV3Response.PollResponse;
  status: number;
  error: false;
};

export type SonarV3CreateSessionPayloadResponse =
  SonarV3CreateSessionPayloadResponseSuccess | SonarV3PayloadResponseError;

export type SonarV3PayloadResponseSuccess =
  SonarV3PollPayloadResponseSuccess | SonarV3CreateSessionPayloadResponseSuccess;

export type SonarV3PayloadResponse =
  SonarV3PollPayloadResponse | SonarV3CreateSessionPayloadResponse;

export type GrpcErrorResponseBody = {
  message: string;
  grpcErrorCode?: number;
};

export type SonarV3PollPayloadResponse =
  SonarV3PollPayloadResponseSuccess | SonarV3PayloadResponseError;

export type SonarV3PayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type PartnerMetadataPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerPartnermetadatasvcV1PartnerMetadata.GetPartnerMetadataResponse;
  status: number;
  error: false;
};

export type PartnerMetadataPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type PricePulsePayloadResponseSuccess = {
  headers: {};
  body: SkyscannerDropsapiV5alphaPricePulse.PricePulseResponse;
  status: number;
  error: false;
};

export type PricePulsePayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type PriceInsightsPriceBandsPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerPriceinsightsV2PriceInsights.SearchPriceBandsResponse;
  status: number;
  error: false;
};

export type PriceInsightsPriceBandsPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type PriceInsightsPriceBandsPayloadResponse =
  PriceInsightsPriceBandsPayloadResponseSuccess | PriceInsightsPriceBandsPayloadResponseError;

export type PartnerMetadataPayloadResponse =
  PartnerMetadataPayloadResponseSuccess | PartnerMetadataPayloadResponseError;

export type PricePulsePayloadResponse =
  PricePulsePayloadResponseSuccess | PricePulsePayloadResponseError;

export type PricePulseRequestInit = RequestInit<
  SkyscannerDropsapiV5alphaPricePulse.PricePulseRequest,
  string
>;

export const PricingOptionStatus = {
  EXPIRED: 'EXPIRED',
  CURRENT: 'CURRENT',
  UNAVAILABLE: 'UNAVAILABLE',
} as const;

export type PriceInsightsPriceBandsRequestInit = RequestInit<
  SkyscannerPriceinsightsV2PriceInsights.SearchPriceBandsRequest,
  string
>;

export type PaymentDiscountPayloadBodySuccess = {
  headers: {};
  body: SkyscannerPmntdiscountpocsvcV1PaymentDiscount.GetPaymentDiscountResponse;
  status: number;
  error: false;
};

export type PaymentDiscountPayloadBodyError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type PaymentDiscountPayloadResponse =
  PaymentDiscountPayloadBodySuccess | PaymentDiscountPayloadBodyError;

export type PlaceholderServicePayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type PlaceholderServicePayloadResponseSuccess = {
  headers: {};
  body: SkyscannerPlaceholderserviceV1PlaceholderService.GetPlaceholdersResponse;
  status: number;
  error: false;
};

export type PlaceholderServicePayloadResponse =
  PlaceholderServicePayloadResponseSuccess | PlaceholderServicePayloadResponseError;

type PriceAlertsPayloadBodySuccess = {
  AlertId: string;
};

type PriceAlertsPayloadBodyError = {
  message: string;
  code: number;
};

export type PriceAlertsPayloadResponseSuccess = {
  headers: {};
  body: PriceAlertsPayloadBodySuccess;
  status: number;
  error: false;
};

export type PriceAlertsPayloadResponseError = {
  headers: {};
  body: PriceAlertsPayloadBodyError;
  status: number;
  error: true;
};

export type PriceAlertsPayloadResponse =
  PriceAlertsPayloadResponseSuccess | PriceAlertsPayloadResponseError;

type PrimitiveVariableValue = string | number | boolean | symbol | null | undefined;
type VariableValue = PrimitiveVariableValue[] | PrimitiveVariableValue;
export type VariablesType = Variables | Variables[] | VariableValue | VariableValue[];

export type Variables = {
  [key: string]: VariablesType;
};

export type RankExplainabilityPayloadBodySuccess = {
  headers: {};
  body: SkyscannerRankexplainabilityV1.GetRankExplainabilityMessageResponse;
  status: number;
  error: false;
};

export type RankExplainabilityPayloadBodyError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type RankExplainabilityPayloadResponse =
  RankExplainabilityPayloadBodySuccess | RankExplainabilityPayloadBodyError;

// only for Acorn
export type AlternativeDatesRequestInit =
  RequestInit<SkyscannerRadarAlternativedatesV1AlternativeDates.AlternativeDatesRequest>;

// only for Acorn
export type AlternativeDatesPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerRadarAlternativedatesV1AlternativeDates.AlternativeDatesResponse;
  status: number;
  error: false;
};

// only for Acorn
export type AlternativeDatesPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

// only for Acorn
export type AlternativeDatesPayloadResponse =
  AlternativeDatesPayloadResponseSuccess | AlternativeDatesPayloadResponseError;

// only for Banana
export type FenryrPriceCalendarPayloadResponse = {
  headers: Headers;
  body: SkyscannerFenryrModelV1PriceCalendar.PriceCalendarResponse;
  status: number;
  error: false;
};

export type FenryrPriceCalendarExplorePayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type FenryrPriceCalendarExplorePayloadResponseSuccess = {
  headers: Headers;
  body: SkyscannerFenryrModelV1PriceCalendarExplore.PriceCalendarExploreResponse;
  status: number;
  error: false;
};

export type FenryrPriceCalendarExplorePayloadResponse =
  FenryrPriceCalendarExplorePayloadResponseSuccess | FenryrPriceCalendarExplorePayloadResponseError;

export type FenryrPriceCalendarMonthPayloadResponse = {
  headers: Headers;
  body: SkyscannerFenryrModelV1PriceCalendarMonth.PriceCalendarMonthResponse;
  status: number;
  error: false;
};

// Shared TravelApi types (only for Acorn)
type TravelApiLocation = {
  name: Record<string, string>;
  id: string;
};

type TravelApiResult = {
  id: string;
  country_code: string;
  iata_code: string;
  rnid: number;
  has_flights: boolean;
  location: TravelApiLocation[];
};

export type TravelApiBodyPayload = {
  results: TravelApiResult[];
};

export type TravelApiPayloadResponseSuccess = {
  headers: Headers;
  body: TravelApiBodyPayload;
  status: number;
  error: false;
};

export type TravelApiPayloadResponse = TravelApiPayloadResponseSuccess;

// Shared Postcard types (only for Acorn)
export type PostcardPayloadResponseSuccess = {
  headers: Headers;
  body: PostcardBodyPayload;
  status: number;
  error: false;
};

type PostcardPayloadResponseError = {
  headers: Headers;
  body: {
    message: string;
  };
  status: number;
  error: true;
};

export type PostcardPayloadResponse = PostcardPayloadResponseSuccess | PostcardPayloadResponseError;

// Shared MangalicaIndicativePrices types (only for Acorn)
export type MangalicaIndicativePricesBodyPayload = {
  isError?: boolean;
  deeplink?: string;
  min_price_overall?: number;
};

export type MangalicaIndicativePricesPayloadResponseSuccess = {
  headers: Headers;
  body: MangalicaIndicativePricesBodyPayload;
  status: number;
  error: false;
};

export type MangalicaIndicativePricesPayloadResponse =
  MangalicaIndicativePricesPayloadResponseSuccess;

// Shared RadarExplore types (only for Banana)
type ConductorHeadersPayload = Record<GatewayServedBy, string>;

export type RadarExplorePayloadResponseSuccess = {
  headers: ConductorHeadersPayload;
  body: RadarExploreResponse;
  status: number;
  error: false;
};

export type RadarExplorePayloadResponseError = {
  headers: ConductorHeadersPayload;
  body: RadarExploreResponse;
  status: number;
  error: true;
};

export type RadarExplorePayloadResponse =
  RadarExplorePayloadResponseSuccess | RadarExplorePayloadResponseError;

// Shared RadarExploreAI types (only for Banana)
export type RadarExploreAIPayloadResponseSuccess = {
  headers: ConductorHeadersPayload;
  body: RadarExploreAIResponse;
  status: number;
  error: false;
};

export type RadarExploreAIPayloadResponseError = {
  headers: ConductorHeadersPayload;
  body: RadarExploreAIResponse;
  status: number;
  error: true;
};

export type RadarExploreAIPayloadResponse =
  RadarExploreAIPayloadResponseSuccess | RadarExploreAIPayloadResponseError;

// Shared AdsCapability types (only for Banana)
export type AdsCapabilityRequestBody =
  SkyscannerAdvertsAdsretrievalserviceV1alpha1Request.GetAdsRequest;
export type AdsCapabilityResponse =
  SkyscannerAdvertsAdsretrievalserviceV1alpha1Response.GetAdsResponse;

export type AdsCapabilityHomepagePayloadResponseSuccess = {
  headers: {};
  body: AdsCapabilityResponse;
  status: number;
  error: false;
};

export type AdsCapabilityHomepagePayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type AdsCapabilityHomepagePayloadResponse =
  AdsCapabilityHomepagePayloadResponseSuccess | AdsCapabilityHomepagePayloadResponseError;

export type AdsCapabilityRequestInit = RequestInit<AdsCapabilityRequestBody, string> & {
  body: AdsCapabilityRequestBody;
};

// Shared AdsCapabilityExplore types (only for Banana) — same wire shape as homepage,
// keyed differently so cache and middleware can distinguish surfaces.
// TODO: QUAR-1652: collapse these aliases into the generalised AdsCapability* types once Potato's hook (#25114) lands.
export type AdsCapabilityExplorePayloadResponseSuccess =
  AdsCapabilityHomepagePayloadResponseSuccess;
export type AdsCapabilityExplorePayloadResponseError = AdsCapabilityHomepagePayloadResponseError;
export type AdsCapabilityExplorePayloadResponse = AdsCapabilityHomepagePayloadResponse;
export type AdsCapabilityExploreRequestInit = AdsCapabilityRequestInit;

// Shared AdsRetrievalServiceNavCard types (only for Banana)
export type AdsRetrievalServiceNavCardPayloadResponse = {
  headers: Headers;
  body: AdsRetrievalServiceNavCardBodyPayload;
  status: number;
  error: false;
};

// Shared AdsRetrievalServiceBookingPanel types (only for Banana)
export type AdsRetrievalServiceBookingPanelPayloadResponse = {
  headers: Headers;
  body: AdsRetrievalServiceBookingPanelBodyPayload;
  status: number;
  error: false;
};

// Shared StrevdaSlots types
export type StrevdaSlotsBodyPayload = Slot[];
export type StrevdaSlotsPayloadResponseSuccess = {
  headers: Headers;
  body: StrevdaSlotsBodyPayload;
  status: number;
  error: false;
};
export type StrevdaSlotsPayloadResponse = StrevdaSlotsPayloadResponseSuccess;

export type AutosuggestSearchPayloadResponseSuccess = {
  headers: {};
  body: FlightsAutosuggestResponse;
  status: number;
  error: false;
};

export type AutosuggestSearchPayloadResponseError = {
  headers: {};
  body: { message?: string };
  status: number;
  error: true;
};

export type AutosuggestSearchPayloadResponse =
  AutosuggestSearchPayloadResponseSuccess | AutosuggestSearchPayloadResponseError;

export type TerraLocationApiItem = SkyscannerTerraserviceDomainV2Location.GeoLocation;

// only for Banana
export type TerraGetLocationsByIdPayloadResponseSuccess = {
  headers: {};
  body: SkyscannerTerraserviceApiV2Lookup.GetLocationsByIdResponse;
  status: number;
  error: false;
};

export type TerraGetLocationsByIdPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type TerraGetLocationsByIdPayloadResponse =
  TerraGetLocationsByIdPayloadResponseSuccess | TerraGetLocationsByIdPayloadResponseError;

export type TerraGetLocationsByIdRequestInit = RequestInit<
  SkyscannerTerraserviceApiV2Lookup.GetLocationsByIdRequest,
  string
>;

export type LocationTagsRequestInit = RequestInit<LocationTagsGetRequest>;

export type LocationTagsPayloadResponseSuccess = {
  headers: Headers;
  body: LocationTagsGetResponse;
  status: number;
  error: false;
};

export type LocationTagsPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type LocationTagsPayloadResponse =
  LocationTagsPayloadResponseSuccess | LocationTagsPayloadResponseError;

// Explore Experiences Service types
export type ExploreExperiencesGetExperiencesRequest =
  SkyscannerExploreexperiencessvcV1Experiences.GetExperiencesRequest;
export type ExploreExperiencesGetExperiencesResponse =
  SkyscannerExploreexperiencessvcV1Experiences.GetExperiencesResponse;
export type ExploreExperience = SkyscannerExploreexperiencessvcV1Experiences.Experience;

export type ExploreExperiencesGetMiamiDolphinsRequest =
  SkyscannerExploreexperiencessvcV1Experiences.GetMiamiDolphinsRequest;
export type ExploreExperiencesGetMiamiDolphinsResponse =
  SkyscannerExploreexperiencessvcV1Experiences.GetMiamiDolphinsResponse;

export type ExploreExperiencesRequestInit = RequestInit<ExploreExperiencesGetExperiencesRequest>;
export type ExploreExperiencesDolphinsRequestInit =
  RequestInit<ExploreExperiencesGetMiamiDolphinsRequest>;

export type ExploreExperiencesPayloadResponseSuccess = {
  headers: {};
  body: ExploreExperiencesGetExperiencesResponse;
  status: number;
  error: false;
};

export type ExploreExperiencesPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type ExploreExperiencesPayloadResponse =
  ExploreExperiencesPayloadResponseSuccess | ExploreExperiencesPayloadResponseError;

export type ExploreExperiencesDolphinsPayloadResponseSuccess = {
  headers: {};
  body: ExploreExperiencesGetMiamiDolphinsResponse;
  status: number;
  error: false;
};

export type ExploreExperiencesDolphinsPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type ExploreExperiencesDolphinsPayloadResponse =
  ExploreExperiencesDolphinsPayloadResponseSuccess | ExploreExperiencesDolphinsPayloadResponseError;

// Explore Pricing Service types (using generated gRPC types)
export type ExplorePricingIndicativeFlightPrice =
  SkyscannerExplorepricingserviceV1ExplorePricing.IndicativeFlightPrice;
export type ExplorePricingIndicativeHotelPrice =
  SkyscannerExplorepricingserviceV1ExplorePricing.IndicativeHotelPrice;
export type ExplorePricingGetIndicativePricesRequest =
  SkyscannerExplorepricingserviceV1ExplorePricing.GetIndicativePricesRequest;
export type ExplorePricingGetIndicativePricesResponse =
  SkyscannerExplorepricingserviceV1ExplorePricing.GetIndicativePricesResponse;
export type ExplorePricingGetMonthlyIndicativePricesRequest =
  SkyscannerExplorepricingserviceV1ExplorePricing.GetMonthlyIndicativePricesRequest;
export type ExplorePricingGetMonthlyIndicativePricesResponse =
  SkyscannerExplorepricingserviceV1ExplorePricing.GetMonthlyIndicativePricesResponse;
export type ExplorePricingMonthlyPriceSummary =
  SkyscannerExplorepricingserviceV1ExplorePricing.MonthlyPriceSummary;
export type ExplorePricingMonthlyHotelPriceSummary =
  SkyscannerExplorepricingserviceV1ExplorePricing.MonthlyHotelPriceSummary;
export type ExplorePricingHotelPriceAggregation =
  SkyscannerExplorepricingserviceV1ExplorePricing.HotelPriceAggregation;

export type ExplorePricingIndicativePricesRequestInit =
  RequestInit<ExplorePricingGetIndicativePricesRequest>;

export type ExplorePricingIndicativePricesPayloadResponseSuccess = {
  headers: {};
  body: ExplorePricingGetIndicativePricesResponse;
  status: number;
  error: false;
};

export type ExplorePricingIndicativePricesPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type ExplorePricingIndicativePricesPayloadResponse =
  | ExplorePricingIndicativePricesPayloadResponseSuccess
  | ExplorePricingIndicativePricesPayloadResponseError;

export type ExplorePricingMonthlyPricesRequestInit =
  RequestInit<ExplorePricingGetMonthlyIndicativePricesRequest>;

export type ExplorePricingMonthlyPricesPayloadResponseSuccess = {
  headers: {};
  body: ExplorePricingGetMonthlyIndicativePricesResponse;
  status: number;
  error: false;
};

export type ExplorePricingMonthlyPricesPayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type ExplorePricingMonthlyPricesPayloadResponse =
  | ExplorePricingMonthlyPricesPayloadResponseSuccess
  | ExplorePricingMonthlyPricesPayloadResponseError;

export type AskSkyscannerNudgeRequestInit = RequestInit<
  SkyscannerAskskyscannerV1Nudge.NudgeRequest,
  string
>;

export type AskSkyscannerNudgePayloadResponseSuccess = {
  headers: {};
  body: SkyscannerAskskyscannerV1Nudge.NudgeResponse;
  status: number;
  error: false;
};

export type AskSkyscannerNudgePayloadResponseError = {
  headers: null;
  body: GrpcErrorResponseBody;
  status: number;
  error: true;
};

export type AskSkyscannerNudgePayloadResponse =
  AskSkyscannerNudgePayloadResponseSuccess | AskSkyscannerNudgePayloadResponseError;
